import { useCallback, useRef, useState } from 'react';
import { type UploadResult, uploadFile } from './api';

export interface Upload {
  id: number;
  name: string;
  progress: number;
  path: string | null;
  error: number | null;
}

export type UploadFn = (projectId: string, file: File, onProgress?: (fraction: number) => void) => Promise<UploadResult>;

export interface Uploads {
  items: Upload[];
  add: (files: Iterable<File>) => void;
  remove: (id: number) => void;
  /** Paths of finished uploads to announce in the next prompt; clears them. */
  take: () => string[];
}

export function useUploads(projectId: string, upload: UploadFn = uploadFile): Uploads {
  const [items, setItems] = useState<Upload[]>([]);
  const nextId = useRef(1);
  const patch = (id: number, change: Partial<Upload>): void => setItems((current) => current.map((item) => (item.id === id ? { ...item, ...change } : item)));

  const add = useCallback(
    (files: Iterable<File>): void => {
      for (const file of files) {
        const id = nextId.current;
        nextId.current += 1;
        setItems((current) => [...current, { id, name: file.name, progress: 0, path: null, error: null }]);
        void upload(projectId, file, (progress) => patch(id, { progress })).then((result) =>
          patch(id, 'path' in result ? { path: result.path, progress: 1 } : { error: result.error }),
        );
      }
    },
    [projectId, upload],
  );

  const remove = useCallback((id: number): void => setItems((current) => current.filter((item) => item.id !== id)), []);

  const take = (): string[] => {
    const paths = items.flatMap((item) => (item.path === null ? [] : [item.path]));
    setItems((current) => current.filter((item) => item.path === null && item.error === null));
    return paths;
  };

  return { items, add, remove, take };
}
